//--------------------------------------------------------------------//
//******************** voicemailGreetingRecorder ***********************//
//--------------------------------------------------------------------//
// Record/preview/save/play/delete a DID's custom voicemail greeting, from
// inside the "Edit DID Number" dialog (trunk-numbers.tab.js). Only rendered
// there when editing an existing DID (ng-if="isEditing") - the server 404s
// the voice-mail-greeting endpoints for a number that isn't already a saved
// did_numbers entry. See
// sip-gudhub-server/docs/sip-trunks/voice-mail-greeting-client-guide.md.
import templateUrl from './voicemail-greeting-recorder.template.html';
import { recordWavBlob, MAX_RECORDING_SECONDS } from './wavRecorder.js';

angular.module('sipTrunksTableModule')

.directive('voicemailGreetingRecorder', ['SipTrunksService', 'GhDialog', 'ghToastService', '$interval', '$sce', 'localizationService',
    function (SipTrunksService, GhDialog, ghToastService, $interval, $sce, localizationService) {
        return {
            restrict: 'E',
            scope: {
                trunkId: '=',
                number: '=',
                didEntry: '='
            },
            controller: ['$scope', function ($scope) {
                const dictionary = localizationService.getDictionary('account_settings');
                $scope.translate = (word) => localizationService.translate(word, dictionary);

                $scope.isRecording = false;
                $scope.isPreviewing = false;
                $scope.isUploading = false;
                $scope.isLoadingPlayback = false;
                // $sce-trusted wrappers for the template's ng-src - blob:
                // URLs aren't in Angular's default RESOURCE_URL whitelist,
                // so an untrusted one throws $sce:insecurl. previewObjectUrl/
                // playbackObjectUrl (below) keep the raw strings for
                // revokeObjectURL, since that call needs an actual string,
                // not the trusted wrapper.
                $scope.previewUrl = null;
                $scope.playbackUrl = null;
                $scope.elapsedLabel = '0:00';
                $scope.errorMessage = '';

                // Not on $scope - nothing in the template needs to bind to
                // these directly, they're just recording/playback machinery.
                let activeStream = null;
                let activeRecorder = null;
                let elapsedTimer = null;
                let elapsedSeconds = 0;
                let pendingBlob = null;
                let previewObjectUrl = null;
                let playbackObjectUrl = null;

                const formatTime = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

                function releaseStream() {
                    if (activeStream) {
                        activeStream.getTracks().forEach((track) => track.stop());
                        activeStream = null;
                    }
                }

                function clearElapsedTimer() {
                    if (elapsedTimer) {
                        $interval.cancel(elapsedTimer);
                        elapsedTimer = null;
                    }
                }

                $scope.startRecording = async function () {
                    $scope.errorMessage = '';

                    try {
                        activeStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                        activeRecorder = recordWavBlob(activeStream);
                    } catch (error) {
                        ghToastService.simple(error.message || 'Microphone access failed');
                        return;
                    }

                    elapsedSeconds = 0;
                    $scope.elapsedLabel = formatTime(0);
                    $scope.isRecording = true;

                    elapsedTimer = $interval(function () {
                        elapsedSeconds++;
                        $scope.elapsedLabel = formatTime(elapsedSeconds);
                        if (elapsedSeconds >= MAX_RECORDING_SECONDS) {
                            $scope.stopRecording();
                        }
                    }, 1000);

                    $scope.$applyAsync();
                };

                $scope.stopRecording = function () {
                    if (!$scope.isRecording) return;

                    clearElapsedTimer();
                    const wavBlob = activeRecorder.stop();
                    releaseStream();
                    activeRecorder = null;

                    if (previewObjectUrl) {
                        URL.revokeObjectURL(previewObjectUrl);
                    }
                    pendingBlob = wavBlob;
                    previewObjectUrl = URL.createObjectURL(wavBlob);
                    $scope.previewUrl = $sce.trustAsResourceUrl(previewObjectUrl);
                    $scope.isRecording = false;
                    $scope.isPreviewing = true;
                    $scope.$applyAsync();
                };

                $scope.discardPreview = function () {
                    if (previewObjectUrl) {
                        URL.revokeObjectURL(previewObjectUrl);
                    }
                    previewObjectUrl = null;
                    $scope.previewUrl = null;
                    pendingBlob = null;
                    $scope.isPreviewing = false;
                };

                $scope.saveRecording = async function () {
                    if (!pendingBlob) return;

                    $scope.isUploading = true;
                    $scope.errorMessage = '';

                    try {
                        const result = await SipTrunksService.uploadVoicemailGreeting($scope.trunkId, $scope.number, pendingBlob);
                        $scope.didEntry.voice_mail_greeting = result.file_id;

                        // The greeting just changed - drop any cached player
                        // and reload it so the new recording shows up
                        // immediately instead of the old one (or nothing).
                        clearPlayback();
                        $scope.discardPreview();
                        loadPlayback();
                    } catch (error) {
                        console.log('Save voicemail greeting error:', error);
                        $scope.errorMessage = error.message || 'Failed to save recording';
                    } finally {
                        $scope.isUploading = false;
                        $scope.$applyAsync();
                    }
                };

                function clearPlayback() {
                    if (playbackObjectUrl) {
                        URL.revokeObjectURL(playbackObjectUrl);
                        playbackObjectUrl = null;
                    }
                    $scope.playbackUrl = null;
                }

                // Fetches the recorded greeting (auth-gated - can't just
                // point <audio src> at the endpoint, see the client guide)
                // and wires it up as an inline player. Called automatically
                // whenever there's a greeting to show (see init below and
                // saveRecording above) rather than behind a manual "Play"
                // click, since the whole point is to show a player, not a
                // button.
                async function loadPlayback() {
                    if (!$scope.didEntry.voice_mail_greeting || playbackObjectUrl) return;

                    $scope.errorMessage = '';
                    $scope.isLoadingPlayback = true;

                    try {
                        const blob = await SipTrunksService.getVoicemailGreetingBlob($scope.trunkId, $scope.number);

                        if (!blob) {
                            // Shouldn't normally happen (the player only
                            // shows once voice_mail_greeting is set), but
                            // stay consistent if it's stale.
                            $scope.didEntry.voice_mail_greeting = null;
                            $scope.errorMessage = 'No greeting recorded for this number';
                        } else {
                            playbackObjectUrl = URL.createObjectURL(blob);
                            $scope.playbackUrl = $sce.trustAsResourceUrl(playbackObjectUrl);
                        }
                    } catch (error) {
                        console.log('Load voicemail greeting error:', error);
                        $scope.errorMessage = error.message || 'Failed to load greeting';
                    } finally {
                        $scope.isLoadingPlayback = false;
                        $scope.$applyAsync();
                    }
                }

                loadPlayback();

                // Styled like the existing "Delete Number" confirm dialog in
                // trunk-numbers.tab.js (same inline-template-string / button
                // classes) for visual consistency.
                $scope.confirmDeleteGreeting = function () {
                    GhDialog.show({
                        position: 'center',
                        toolbar: false,
                        template: {
                            toolbar: '',
                            resizable: false,
                            content: `
                                <div style="padding: 20px; position: relative; min-width: 360px; text-align: center;">
                                    <span gh-icon="cross 0D99FF 20px" style="position: absolute; top: 4px; right: 4px; cursor: pointer;" ng-click="cancel()"></span>
                                    <h3 style="font-size: 18px; font-weight: 500; margin-bottom: 12px; color: #333;">{{translate('Delete Greeting')}}</h3>
                                    <p style="font-size: 14px; color: #666; margin-bottom: 20px;">{{translate('Are you sure you want to delete this voicemail greeting?')}}</p>
                                    <div style="display: flex; gap: 10px; justify-content: center;">
                                        <button class="btn btn-blue-reverse" ng-click="cancel()">{{translate('Cancel')}}</button>
                                        <button class="btn btn-red" ng-click="confirm()" ng-disabled="deleting">{{translate('Delete')}}</button>
                                    </div>
                                </div>
                            `
                        },
                        controller: ['$scope', function (dialogScope) {
                            dialogScope.translate = $scope.translate;
                            dialogScope.deleting = false;

                            dialogScope.confirm = async function () {
                                dialogScope.deleting = true;

                                try {
                                    await SipTrunksService.deleteVoicemailGreeting($scope.trunkId, $scope.number);
                                    $scope.didEntry.voice_mail_greeting = null;
                                    clearPlayback();

                                    dialogScope.hide();
                                    $scope.$applyAsync();
                                } catch (error) {
                                    console.log('Delete voicemail greeting error:', error);
                                    dialogScope.deleting = false;
                                    dialogScope.$applyAsync();
                                }
                            };
                        }]
                    });
                };

                // GhDialog's hide()/cancel() destroy the dialog scope this
                // directive lives under (see gui/dialog/gh_dialog.js), which
                // tears this directive's isolate scope down too - e.g. if
                // the admin hits the dialog's X while mid-recording. Make
                // sure the mic actually gets released and blob URLs don't
                // leak.
                $scope.$on('$destroy', function () {
                    clearElapsedTimer();
                    releaseStream();
                    if (previewObjectUrl) URL.revokeObjectURL(previewObjectUrl);
                    if (playbackObjectUrl) URL.revokeObjectURL(playbackObjectUrl);
                });
            }],
            templateUrl
        };
    }]);
